import { BlockingAppCandidatesDocument } from 'src/graphql/actions/apps/blockingAppCandidates.query';
import { AppActiveProtectionIdsDocument } from 'src/graphql/actions/apps/appActiveProtectionIds.query';

import type { ApolloClient } from '@apollo/client';
import type { IBlockingApp } from 'src/slices/cancellationPlanSlice';

const PER_PAGE = 50;

const loadCandidatesPage = async (client: ApolloClient, page: number) => {
  const { data } = await client.query({
    query: BlockingAppCandidatesDocument,
    variables: { page, perPage: PER_PAGE },
    fetchPolicy: 'network-only',
  });

  return { nodes: data?.apps.nodes ?? [], pagesCount: data?.apps.pagesCount ?? 1 };
};

// Page 1 tells us pagesCount; the remaining pages are fetched in parallel.
const loadCandidates = async (client: ApolloClient) => {
  const { nodes, pagesCount } = await loadCandidatesPage(client, 1);
  const remainingPages = Array.from({ length: Math.max(pagesCount - 1, 0) }, (_, index) => index + 2);
  const restPages = await Promise.all(remainingPages.map((page) => loadCandidatesPage(client, page)));

  return [...nodes, ...restPages.flatMap((page) => page.nodes)];
};

const loadActiveProtectionsCount = async (client: ApolloClient, appId: string) => {
  const { data } = await client.query({
    query: AppActiveProtectionIdsDocument,
    variables: { appId },
    fetchPolicy: 'network-only',
  });

  return data?.appProtections.length ?? 0;
};

// Owned apps that still have active protections, which block plan cancellation. Sorted by count, descending.
export const loadBlockingApps = async (client: ApolloClient): Promise<IBlockingApp[]> => {
  const candidates = await loadCandidates(client);

  const blockingApps = await Promise.all(
    candidates.map(async ({ id, name, logoUrl, unclaimed }) => ({
      id,
      name,
      logoUrl,
      unclaimed,
      activeProtectionsCount: await loadActiveProtectionsCount(client, id),
    })),
  );

  return blockingApps
    .filter(({ activeProtectionsCount }) => activeProtectionsCount > 0)
    .sort((a, b) => b.activeProtectionsCount - a.activeProtectionsCount);
};
